import { KeyValuePipe, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { Component, input, ViewEncapsulation } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatError, MatFormField, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { map, of, startWith, switchMap } from 'rxjs';

import { CelumFormFieldDirective } from '@celum/common-components/inputs';
import { CelumPropertiesProvider } from '@celum/core/configuration';
import { LanguageChooserComponent } from '@celum/internal-components/language-chooser';
import { FnWrapperPipe } from '@celum/ng2base/pipes';

@Component({
  selector: 'localized-field',
  templateUrl: './localized-field.component.html',
  styleUrls: ['./localized-field.component.scss', '../format-view-shared.scss'],
  encapsulation: ViewEncapsulation.None, // custom style for `celum-form-field size="medium"`
  imports: [
    KeyValuePipe,
    MatError,
    MatFormField,
    MatHint,
    MatInput,
    NgTemplateOutlet,
    ReactiveFormsModule,
    TranslatePipe,
    UpperCasePipe,

    LanguageChooserComponent,
    FnWrapperPipe,
    CelumFormFieldDirective
  ]
})
export class LocalizedFieldComponent {
  public formGroup = input<FormGroup<Record<string, FormControl<string>>>>();
  public validationLimit = input<number>();
  public inputType = input<'input' | 'textarea'>();
  public fieldName = input<string>();
  public placeholder = input<string>();

  protected readonly availableLanguages = CelumPropertiesProvider.properties.appProperties.formats?.supportedLocales;

  protected readonly activeLanguages = toSignal(
    toObservable(this.formGroup).pipe(
      switchMap(group => (group ? group.valueChanges.pipe(startWith(group.value)) : of(undefined))),
      map(() => new Set(Object.keys(this.formGroup()?.controls ?? {})))
    ),
    { initialValue: new Set<string>() }
  );

  protected activeLanguagesChanged({ key, checked }: { key: string; checked: boolean }): void {
    if (checked) {
      this.formGroup().addControl(key, new FormControl('', Validators.maxLength(this.validationLimit())));
    } else {
      this.formGroup().removeControl(key);
    }
  }

  protected hasRequiredValidator(formControl: FormControl): boolean {
    return formControl.hasValidator(Validators.required);
  }
}
